import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Caret from 'components/Select/caret';
import Icon from 'components/Icon';
import Heading from 'components/Heading';

import useOutisdeClick from './useOutsideClick';

import {
  OutsideWrapper,
  SelectMainWrapper,
  MainTitleWrapper,
  ContentWrapper,
  StyledLink,
  RowWrapper,
  RowColumnWrapper,
  WhiteBar,
  TableWhiteBar,
} from './Select.styled';

export const stylesTypes = {
  RECTANGLE: 'rectangle',
  TABLE: 'table',
  MINIMAL: 'minimal',
  BURGER: 'burger',
};

export default function Select(props) {
  const {
    type,
    multiple,
    title,
    subtitle,
    options,
    main,
    positionFixed,
    parentScroll,
  } = props;

  const [selected, setSelected] = useState(props.defautSelectSingle);
  const [selectedList, setSelectedList] = useState([]);
  const { ref, isOpen, setOpen } = useOutisdeClick(false);

  const updateMultipleSelection = index => {
    if (selectedList.includes(index)) {
      setSelectedList(selectedList.filter(value => value !== index));
      return;
    }

    setSelectedList([...selectedList, index]);
  };

  const updateSelection = index => {
    if (multiple) {
      updateMultipleSelection(index);
      return;
    }

    setSelected(index);
  };

  useEffect(() => {
    if (parentScroll && isOpen) setOpen(false);
  }, [parentScroll]);

  const optionsOutput =
    Array.isArray(options) &&
    options.map((obj, index) => {
      const { icon, subLabel, route = null } = obj;
      const optionRow = (
        <RowWrapper
          key={index}
          type={type}
          selected={
            multiple ? selectedList.includes(index) : index === selected
          }
          onClick={() => updateSelection(index)}
        >
          {icon && <Icon name={icon} size='medium' margin='0 default 0 0' />}
          <RowColumnWrapper>
            <Heading
              singleLine
              key='title'
              level='5'
              size={type === 'table' ? 'xsmall' : 'small'}
              weight='regular'
            >
              {obj?.label}
            </Heading>
            {subLabel && (
              <Heading
                singleLine
                key='subtitle'
                level='6'
                size='xsmall'
                weight='regular'
                variant='minimal'
                margin='small 0'
              >
                <i>{subLabel}</i>
              </Heading>
            )}
          </RowColumnWrapper>
        </RowWrapper>
      );
      if (route) {
        return (
          <StyledLink name={route} key={index}>
            {optionRow}
          </StyledLink>
        );
      }
      return optionRow;
    });

  const selectMain = !main && (
    <SelectMainWrapper
      type={type}
      margin={props.margin}
      onClick={() => setOpen(!isOpen)}
    >
      {type === 'rectangle' && (
        <WhiteBar type={type} isOpen={isOpen}>
          <div />
        </WhiteBar>
      )}
      <MainTitleWrapper>
        <Heading
          key='title'
          singleLine
          level='5'
          size={type === 'table' ? 'xsmall' : 'default'}
          weight={subtitle || type === 'table' ? 'xbold' : 'regular'}
        >
          {title && !multiple ? title : options[selected].label}
        </Heading>
        {subtitle && (
          <Heading
            key='subtitle'
            singleLine
            level='5'
            size={type === 'table' ? 'small' : 'default'}
            weight='regular'
            margin='1rem 0'
          >
            {subtitle}
          </Heading>
        )}
        <Caret show={isOpen} size='small' />
      </MainTitleWrapper>
    </SelectMainWrapper>
  );

  return (
    <OutsideWrapper ref={ref}>
      {main ? <div onClick={() => setOpen(!isOpen)}>{main}</div> : selectMain}
      <ContentWrapper
        type={type}
        margin={props.margin}
        isOpen={isOpen}
        positionFixed={positionFixed}
        top={positionFixed ? ref?.current?.offsetTop : 0}
        parentScroll={parentScroll || 0}
      >
        {type === 'table' && (
          <>
            {selectMain}
            <TableWhiteBar>
              <div />
            </TableWhiteBar>
          </>
        )}
        {optionsOutput}
      </ContentWrapper>
    </OutsideWrapper>
  );
}

Select.defaultProps = {
  type: stylesTypes.RECTANGLE,
  title: '',
  subtitle: '',
  defautSelectSingle: 0,
  multiple: false,
  positionFixed: false,
  options: [],
};

Select.propTypes = {
  /**
   * It changes the style of the component, if we have more than one
   */
  type: PropTypes.oneOf(Object.values(stylesTypes)),
  /**
   * Main title
   */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * Main subtitle
   */
  subtitle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  /**
   * It can be set a default selected index only for the single selection mode
   */
  defautSelectSingle: PropTypes.number,
  /**
   * By default we just can select one option, if multiple prop is true we can select multiple options
   */
  multiple: PropTypes.bool,
  /**
   * This will make the dropdown section be position fixed and use the offsetTop to position it
   */
  positionFixed: PropTypes.bool,
  /**
   * If we pass this prop and the parent scroll number changes if the dropdown is open it will close to avoid overflow issues
   */
  parentScroll: PropTypes.number,
  /**
   * List of objects for each option
   */
  options: PropTypes.arrayOf(PropTypes.object),
  /**
   * The same as margin CSS property using a list of predefined sizes (theme.spacing). For example 'default 0' will be the same as '1rem 0'. The theme.spacing keys will be replaced by the correspondent value.
   */
  margin: PropTypes.string,
  /**
   * If we pass a component prop called main it will replace the section by it
   */
  main: PropTypes.node,
};
